import { isValid, parseISO, format } from 'date-fns';
import i18n from 'i18n-js';
import { ReservationBlockedDate } from 'store/restaurantDetailsStore/reducer';

export interface BlockedDateIssue {
    Key: string;
    From?: string;
    To?: string;
}

export function getBlockedDateReason(entries: ReservationBlockedDate[] | undefined, date: string) {
    return entries?.find(
        (entry) => entry.fromDate <= date && date <= (entry.toDate ?? entry.fromDate)
    )?.reason;
}

export function formatBlockedDateIssue(issue: BlockedDateIssue): string {
    const displayDate = (date?: string) =>
        date ? format(parseISO(date), 'dd.MM.yyyy') : '';
    return i18n.t(`restaurant.reservationSettings.blockedDates.${issue.Key}`, {
        from: displayDate(issue.From),
        to: displayDate(issue.To),
    });
}

export function validateBlockedDates(
    entries: ReservationBlockedDate[],
    saved: ReservationBlockedDate[],
    today: string
): Array<{ reason?: string }> {
    const validDate = (date: string) =>
        /^\d{4}-\d{2}-\d{2}$/.test(date) && isValid(parseISO(date));
    return entries.map((entry) => {
        if (saved.some((original) =>
            original.id === entry.id && original.fromDate === entry.fromDate &&
            original.toDate === entry.toDate && original.reason === entry.reason
        )) return {};
        const issues: BlockedDateIssue[] = [];
        if (!entry.fromDate) issues.push({ Key: 'fromRequired' });
        else if (!validDate(entry.fromDate)) issues.push({ Key: 'invalidDate' });
        if (entry.toDate !== null && !validDate(entry.toDate)) issues.push({ Key: 'invalidDate' });
        if (!entry.reason.trim()) issues.push({ Key: 'reasonRequired' });
        else if (entry.reason.length > 100) issues.push({ Key: 'reasonTooLong' });
        if (validDate(entry.fromDate) && (entry.toDate === null || validDate(entry.toDate))) {
            const lastDay = entry.toDate ?? entry.fromDate;
            if (lastDay < entry.fromDate) issues.push({ Key: 'toBeforeFrom' });
            if (lastDay < today) issues.push({ Key: 'past' });
            const other = entries.find((other) =>
                other.id !== entry.id && validDate(other.fromDate) &&
                (other.toDate === null || validDate(other.toDate)) &&
                entry.fromDate <= (other.toDate ?? other.fromDate) && other.fromDate <= lastDay
            );
            if (other) issues.push({
                Key: 'overlap', From: other.fromDate, To: other.toDate ?? other.fromDate,
            });
        }
        return issues.length ? { reason: issues.map(formatBlockedDateIssue).join('\n') } : {};
    });
}
